<template>
  <div
    class="body-2-bold text-static-default-hi mood-main bg-static-brand-hi overflow-hidden"
  >
    <div
      ref="viewport"
      class="scrollbar-none flex h-32 items-center overflow-hidden whitespace-nowrap"
    >
      <div
        ref="track"
        :class="['flex shrink-0 items-center', $style.marquee]"
        :style="{ '--marquee-duration': `${durationSeconds}s` }"
      >
        <!-- The track is two identical copies. The animation translates it by
             -50% (exactly one copy width), so the second copy lands precisely
             where the first started — a continuous, seamless loop. Each copy is
             the reassurance items repeated enough times to overflow the
             viewport, so the ticker always fills the width and never shows a
             gap on wide screens. -->
        <ul ref="firstCopy" class="flex shrink-0 items-center gap-16 pr-16">
          <li
            v-for="(item, index) in repeatedItems"
            :key="`a-${item.id}-${index}`"
            class="flex shrink-0 items-center gap-16"
          >
            <span aria-hidden="true">•</span>
            <span>{{ i18n(item) }}</span>
          </li>
        </ul>
        <ul aria-hidden="true" class="flex shrink-0 items-center gap-16 pr-16">
          <li
            v-for="(item, index) in repeatedItems"
            :key="`b-${item.id}-${index}`"
            class="flex shrink-0 items-center gap-16"
          >
            <span aria-hidden="true">•</span>
            <span>{{ i18n(item) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './OutletReassuranceBanner.translations'

// Constant scroll speed (px/sec). The animation duration is derived from the
// rendered width so the ticker glides at the same pace on every viewport.
const SCROLL_SPEED = 40

const i18n = useI18n()

const items = [
  translations.warranty,
  translations.returns,
  translations.inspection,
]

const viewport = useTemplateRef<HTMLElement>('viewport')
const track = useTemplateRef<HTMLElement>('track')
const firstCopy = useTemplateRef<HTMLElement>('firstCopy')

// How many times to repeat the base items within a single copy so it spans at
// least the viewport width. Self-corrects on mount and on resize.
const repeats = ref(1)
const durationSeconds = ref(20)

const repeatedItems = computed(() =>
  Array.from({ length: repeats.value }, () => items).flat(),
)

function measure() {
  if (!viewport.value || !firstCopy.value) return

  const copyWidth = firstCopy.value.scrollWidth
  if (copyWidth === 0) return

  const baseSetWidth = copyWidth / repeats.value
  // +1 base set of margin so a single copy always overflows the viewport,
  // which keeps the -50% loop seamless.
  const needed = Math.max(
    1,
    Math.ceil(viewport.value.clientWidth / baseSetWidth) + 1,
  )

  if (needed !== repeats.value) {
    repeats.value = needed

    return
  }

  // One copy travels its own width over the loop; keep the px/sec pace fixed.
  durationSeconds.value = Math.max(1, copyWidth / SCROLL_SPEED)
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined' && viewport.value) {
    resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(viewport.value)
    resizeObserver.observe(track.value as HTMLElement)
  }
})

onUnmounted(() => resizeObserver?.disconnect())
</script>

<style module>
@keyframes marquee-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.marquee {
  animation: marquee-scroll var(--marquee-duration, 20s) linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .marquee {
    animation: none;
  }
}
</style>
